import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";

import { VenueCard } from "../../common/VenueCard";
import { ActionButton } from "../../common/ActionButton";
import { VenueDataFragment } from "../Venue";
import { EventCardFragment } from "../../common/EventCard";
import { EventsListing } from "./EventsListing";
import { MapElement } from "../../Layout/MapElement";
import { Marker } from "../../Layout/MapElement/Marker";
import { ReactComponent as MapBorderSvg } from "./mapwrap.svg";

import styles from "./Map.module.scss";
import { useAppContext } from "../../Context/AppContext";
import { Expandable } from "../../common/Expandable";
import { FilterObject, FilterOptions } from "../../common/FilterOptions";
import { filterVenues } from "../../../utils/data";
import { event } from "../../../utils/analytics";

export interface VenueCardFragment
  extends Pick<
    VenueDataFragment,
    "image" | "name" | "status" | "description" | "tags" | "location"
  > {
  slug: string;
}

export interface MapPageProps {
  items: Array<
    VenueCardFragment & {
      whats_on: string[];
    }
  >;
  events: EventCardFragment[];
}

enum TabState {
  Venues,
  Whats_On,
}

const MapPage: React.FC<MapPageProps> = ({ items, events }) => {
  const { animating } = useAppContext();

  const [active, setActive] = useState<number>(-1);
  const [tab, setTab] = useState<TabState>(TabState.Venues);
  const [showMap, setShowMap] = useState<boolean>(true);
  const listRef = useRef<HTMLDivElement>(null);

  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState<FilterObject>({});

  const isBig = useMediaQuery({
    query: "(min-width: 48em)",
  });

  const eventsExpanded = useMemo(() => {
    return events.map(evt => ({
      ...evt,
      venue: items.find(item => {
        return item.whats_on?.includes(evt.specific);
      }),
    }));
  }, [items, events]);

  const venues = useMemo(() => {
    return filterVenues(items, filterOptions);
  }, [filterOptions, items]);

  const onFilter = useCallback(filterObject => {
    setFilterOptions(filterObject);
  }, []);

  const activeLocation = useMemo(() => {
    if (active < 0) {
      return undefined;
    }

    return items[active - 1].location;
  }, [items, active]);

  useEffect(() => {
    if (active >= 0 && listRef.current !== null) {
      const listEl = listRef.current as HTMLDivElement;
      const targetEl = listEl.querySelector<HTMLElement>(
        `.Map-module--card--3X2OG:nth-child(${active})`
      );

      if (!targetEl) {
        return;
      }

      listEl.scrollTo({
        top: targetEl.offsetTop - listEl.offsetTop,
        behavior: "smooth",
      });
    }
  }, [active]);

  const onMarkerActive = useCallback((num, force) => {
    if (force) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setActive(num);
        });
      });
    } else if (force === false) {
      setActive(-1);
    } else {
      setActive(activeNum => (activeNum === num ? -1 : num));
    }
  }, []);

  return (
    <section
      className={classNames(styles.wrapper, { [styles.showMap]: showMap })}
    >
      {((!isBig && !showMap) || isBig) && (
        <>
          {eventsExpanded.length > 0 && (
            <div className={classNames(styles.meta, styles.metaLine)}>
              <ActionButton
                className={styles.metaBtn}
                active={tab === TabState.Venues}
                onClick={() => {
                  event({
                    action: "click",
                    category: "Venues tab",
                  });
                  setTab(TabState.Venues);
                }}
              >
                All Venues
              </ActionButton>
              <ActionButton
                className={styles.metaBtn}
                active={tab === TabState.Whats_On}
                onClick={() => {
                  event({
                    action: "click",
                    category: "Whos on tab",
                  });
                  setTab(TabState.Whats_On);
                }}
              >
                What's on?
              </ActionButton>
            </div>
          )}

          <div className={styles.filterWrapper}>
            <button
              className={styles.filterBtn}
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <span>Filter</span>
              <span>{filterOpen ? "-" : "+"}</span>
            </button>
            <Expandable innerClassName={styles.filterSection} open={filterOpen}>
              <FilterOptions onFilter={onFilter} />
            </Expandable>
          </div>

          <aside ref={listRef} className={styles.list}>
            {tab === TabState.Venues &&
              venues.map((venue, idx) => (
                <VenueCard
                  key={venue.slug}
                  {...venue}
                  active={active === venue.number}
                  number={venue.number}
                  className={styles.card}
                  display={!animating}
                >
                  {venue.description}
                </VenueCard>
              ))}

            {tab === TabState.Whats_On && (
              <EventsListing events={eventsExpanded} />
            )}
          </aside>
        </>
      )}

      {((!isBig && showMap) || isBig) && (
        <>
          <div className={styles.map}>
            <MapBorderSvg
              className={styles.mapBorder}
              preserveAspectRatio="none"
            />
            {!animating && (
              <MapElement center={activeLocation?.coordinates}>
                {venues.map((venue, idx) => (
                  <Marker
                    key={venue.slug}
                    position={venue.location.coordinates}
                    title={venue.name}
                    description={venue.description}
                    to={venue.slug}
                    number={venue.number}
                    onActive={onMarkerActive}
                  />
                ))}
              </MapElement>
            )}
          </div>
        </>
      )}

      {!isBig && (
        <div
          className={classNames(
            styles.meta,
            styles.metaLine,
            styles.metaLineTop
          )}
        >
          <ActionButton
            className={styles.metaBtn}
            active={showMap}
            notLast
            onClick={() => setShowMap(true)}
          >
            Map
          </ActionButton>
          <ActionButton
            className={styles.metaBtn}
            active={!showMap}
            onClick={() => setShowMap(false)}
          >
            List
          </ActionButton>
        </div>
      )}
    </section>
  );
};

export { MapPage };
